import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowUp } from "lucide-react";

export default function FloatingActions() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollPct = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      setShowTop(scrollPct > 30);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-center gap-3">
      {/* Chat routes to the in-app Support page. WhatsApp / phone quick-dials
          intentionally live on Support.jsx / Footer.jsx now — the floating
          stack only surfaces the primary Chat entry point. */}
      <FloatBtn
        testid="float-chat"
        icon={MessageCircle}
        label="Chat with support"
        color="#4B2AAD"
        to="/support"
      />
      {showTop && (
        <button
          type="button"
          data-testid="float-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group flex items-center justify-center h-12 w-12 rounded-full bg-white border border-gray-200 shadow-lg hover:bg-[#4B2AAD] hover:border-[#4B2AAD] transition-all"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5 text-gray-700 group-hover:text-white transition-colors" />
        </button>
      )}
    </div>
  );
}

// Renders as an anchor when `href` is supplied (external protocols such as
// tel: or wa.me), as a React Router Link when `to` is supplied (in-app
// routes), and falls back to a plain button otherwise.
function FloatBtn({ icon: Icon, label, color, testid, href, to, external }) {
  const className =
    "group relative flex items-center justify-center h-12 w-12 rounded-full shadow-lg text-white transition-transform hover:scale-110 active:scale-95";

  const inner = (
    <>
      <Icon className="h-5 w-5" />
      <span className="absolute right-full mr-3 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        {label}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        data-testid={testid}
        href={href}
        className={className}
        style={{ background: color }}
        aria-label={label}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  }

  if (to) {
    return (
      <Link
        data-testid={testid}
        to={to}
        className={className}
        style={{ background: color }}
        aria-label={label}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-testid={testid}
      className={className}
      style={{ background: color }}
      aria-label={label}
    >
      {inner}
    </button>
  );
}
